import { createFileRoute, useNavigate } from "@tanstack/react-router";
import styles from "@/styles/Bookings.module.scss";
import { Breadcrumbs } from "@app-components";
import { useTranslation } from "react-i18next";
import MeetingRoomForm, {
  MeetingRoomFormSubmitProps,
} from "@/components/bookings/MeetingRoomForm";
import { createMeetingRoom } from "@/api";
import { MeetingRoom, NotificationType } from "@/types";
import { useSnackbarStore } from "@/store/zustandStore";
import { useQueryClient } from "@tanstack/react-query";
import { getBuildingId, notifyApiError } from "@/lib";
import { useUploadRoomImages } from "@/hooks/useUploadRoomImages";
import { useState } from "react";
import { useWelcomeBookingAccess } from "@/hooks/useWelcomeBookingAccess";
import PageBackButton from "@/components/PageBackButton";

export const Route = createFileRoute("/_portal/building/settings/room/create")({
  component: CreateMeetingRoom,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    combined?: string;
  } => ({
    combined: typeof search.combined === "string" ? search.combined : undefined,
  }),
});

function CreateMeetingRoom() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { combined } = Route.useSearch();
  const isCombinedMeetingRoom = combined === "true";
  const queryClient = useQueryClient();
  const { allowWelcomeBookingAccess } = useWelcomeBookingAccess();
  const { addNotification } = useSnackbarStore();
  const uploadRoomImages = useUploadRoomImages();
  const [submitSucceeded, setSubmitSucceeded] = useState(false);

  const pageTitle = `${t("NEW")}${isCombinedMeetingRoom ? ` ${t("COMBINED").toLowerCase()} ` : " "}${t("BOOKINGS.MODAL.LABELS.ROOM").toLowerCase()}`;

  const breadcrumbs = [
    {
      label: t("SIDEBAR.BUILDING_SETTINGS"),
      path: "/building/settings",
    },
    {
      label: "Welcome Booking",
      path: "/building/settings/booking",
    },
    {
      label: pageTitle,
      path: `/building/settings/room/create`,
    },
  ];

  const handleOnSubmit = async ({ value }: MeetingRoomFormSubmitProps) => {
    const { thumbnail, images, ...rest } = value;
    const reqBody = rest as MeetingRoom;

    const uploaded = await uploadRoomImages({ thumbnail, images });
    // User chose to go back and fix the failed images.
    if (!uploaded) return;

    reqBody.thumbnail = uploaded.thumbnail;
    reqBody.images = uploaded.images.filter((url) => url !== null);

    try {
      const res = await createMeetingRoom(reqBody);
      setSubmitSucceeded(true);
      addNotification(
        t(`NOTIFICATIONS.${res.data.code}`, {
          entity: t("BOOKINGS.MODAL.LABELS.ROOM").toLowerCase(),
        }),
        NotificationType.SUCCESS,
      );
      queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "meeting-rooms"],
      });
      queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "meeting-room-search"],
      });
      navigate({ to: "/building/settings/booking" });
    } catch (error) {
      console.error(error);
      notifyApiError(error, addNotification, t, {
        properties: {
          entity: t("BOOKINGS.MODAL.LABELS.ROOM").toLowerCase(),
        },
        fallbackKey: "CREATE_FAILED",
      });
    }
  };

  if (!allowWelcomeBookingAccess) return null;

  return (
    <div className={styles.container}>
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton fallbackPath={{ to: "/building/settings/booking" }} />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      <div className="pageHeader">
        <h1>{pageTitle}</h1>
        {isCombinedMeetingRoom && (
          <p>
            {t("BUILDING_SETTINGS.BOOKING.EDIT_ROOM.COMBINED_ROOM.CREATE_INFO")}
          </p>
        )}
      </div>
      <MeetingRoomForm
        isCombinedMeetingRoom={isCombinedMeetingRoom}
        submitSucceeded={submitSucceeded}
        onSubmit={handleOnSubmit}
      />
    </div>
  );
}
